import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { FeeAmount, Pool } from '@kodiak-finance/v3-sdk'
import JSBI from 'jsbi'
import { useMemo } from 'react'

import { useV3PoolsByAddresses } from './subgraph/v3'

export enum PoolState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID,
}

export interface V3PositionDetails {
  pool: Pool
  liquidityTokenBalance: CurrencyAmount<Token>
  totalSupply: CurrencyAmount<Token>
  valueUSD: number
}

export interface UseV3PoolByAddressResults {
  loading: boolean
  position: V3PositionDetails | undefined
  refetch: () => void
}

/**
 * Hook to fetch a single V3 pool by address
 */
export function useV3Pool(poolAddress?: string | null, chainId?: number | null): [PoolState, Pool | null] {
  const {
    data: poolsData,
    isLoading,
    isFetching,
  } = useV3PoolsByAddresses(poolAddress ? [poolAddress] : [], { staleTime: 300 * 1000 })

  return useMemo((): [PoolState, Pool | null] => {
    if (!poolAddress || !chainId) return [PoolState.INVALID, null]
    if (isLoading) return [PoolState.LOADING, null]

    const normalizedAddress = poolAddress.toLowerCase()
    const poolData = poolsData?.pools?.find((pool: { id: string }) => pool.id.toLowerCase() === normalizedAddress)

    if (!poolData) {
      return [isFetching ? PoolState.LOADING : PoolState.NOT_EXISTS, null]
    }

    try {
      const token0 = new Token(
        chainId,
        poolData.token0.id,
        parseInt(poolData.token0.decimals),
        poolData.token0.symbol,
        poolData.token0.name
      )

      const token1 = new Token(
        chainId,
        poolData.token1.id,
        parseInt(poolData.token1.decimals),
        poolData.token1.symbol,
        poolData.token1.name
      )

      const [tokenA, tokenB] = token0.sortsBefore(token1) ? [token0, token1] : [token1, token0]

      const fee = parseInt(poolData.feeTier) as FeeAmount
      const sqrtPriceX96 = JSBI.BigInt(poolData.sqrtPrice)
      const liquidity = JSBI.BigInt(poolData.liquidity)
      const tick = parseInt(poolData.tick)

      // Validate critical pool parameters
      if (!sqrtPriceX96 || JSBI.EQ(sqrtPriceX96, JSBI.BigInt(0))) {
        return [PoolState.NOT_EXISTS, null]
      }

      const pool = new Pool(tokenA, tokenB, fee, sqrtPriceX96, liquidity, tick)

      return [PoolState.EXISTS, pool]
    } catch (error) {
      console.error('Error processing V3 pool data:', error)
      return [PoolState.INVALID, null]
    }
  }, [poolsData, isLoading, poolAddress, chainId])
}

/**
 * Hook to fetch multiple V3 pools by addresses
 */
export function useV3Pools(poolAddresses: string[], chainId?: number | null): [PoolState, Pool | null][] {
  const { data: poolsData, isLoading, isFetching } = useV3PoolsByAddresses(poolAddresses, { staleTime: 300 * 1000 })

  return useMemo(() => {
    if (!chainId) {
      return poolAddresses.map(() => [PoolState.INVALID, null])
    }

    return poolAddresses.map((poolAddress, index) => {
      if (isLoading) return [PoolState.LOADING, null]
      if (!poolAddress) return [PoolState.INVALID, null]

      // Find the pool data for this address (GraphQL doesn't guarantee order)
      const normalizedAddress = poolAddress.toLowerCase()
      const poolData = poolsData?.pools?.find((pool: { id: string }) => pool.id.toLowerCase() === normalizedAddress)
      if (!poolData) return [isFetching ? PoolState.LOADING : PoolState.NOT_EXISTS, null]

      try {
        const token0 = new Token(
          chainId,
          poolData.token0.id,
          parseInt(poolData.token0.decimals),
          poolData.token0.symbol,
          poolData.token0.name
        )

        const token1 = new Token(
          chainId,
          poolData.token1.id,
          parseInt(poolData.token1.decimals),
          poolData.token1.symbol,
          poolData.token1.name
        )

        // Ensure tokens are properly ordered (token0 < token1)
        const [tokenA, tokenB] = token0.sortsBefore(token1) ? [token0, token1] : [token1, token0]

        const fee = parseInt(poolData.feeTier) as FeeAmount
        const sqrtPriceX96 = JSBI.BigInt(poolData.sqrtPrice)
        const liquidity = JSBI.BigInt(poolData.liquidity)
        const tick = parseInt(poolData.tick)

        // Validate critical pool parameters
        if (!sqrtPriceX96 || JSBI.EQ(sqrtPriceX96, JSBI.BigInt(0))) {
          return [PoolState.NOT_EXISTS, null]
        }

        const pool = new Pool(tokenA, tokenB, fee, sqrtPriceX96, liquidity, tick)

        return [PoolState.EXISTS, pool]
      } catch (error) {
        console.error(`Error processing V3 pool data for address ${poolAddress}:`, error)
        return [PoolState.INVALID, null]
      }
    })
  }, [poolsData, isLoading, poolAddresses, chainId])
}
